(() => {
  const ready = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
      return;
    }

    callback();
  };

  const copyTargetsSelector = [
    '.arb-featured-product__copy .arb-eyebrow',
    '.arb-featured-product__copy .arb-display',
    '.arb-featured-product__copy p',
    '.arb-featured-product__copy .arb-check-list li',
    '.arb-featured-product__copy .arb-button',
  ].join(',');

  const runningAnimations = new WeakMap();

  function animateElement(element, keyframes, options) {
    runningAnimations.get(element)?.cancel();

    const animation = element.animate(keyframes, {
      fill: 'both',
      ...options,
    });

    runningAnimations.set(element, animation);
    animation.finished
      .then(() => {
        if (runningAnimations.get(element) === animation) {
          runningAnimations.delete(element);
        }
      })
      .catch(() => {});

    return animation;
  }

  function getCopyTargets(slide) {
    if (!(slide instanceof HTMLElement)) {
      return [];
    }

    return Array.from(slide.querySelectorAll(copyTargetsSelector));
  }

  function setTargetsHidden(slide) {
    getCopyTargets(slide).forEach((target) => {
      target.style.opacity = '0';
      target.style.transform = 'translateY(1.5em)';
      target.style.visibility = 'hidden';
    });
  }

  function clearTargets(slide) {
    getCopyTargets(slide).forEach((target) => {
      target.style.opacity = '';
      target.style.transform = '';
      target.style.visibility = '';
    });
  }

  function resetTimerDots(root, index) {
    root.querySelectorAll('[data-featured-dot]').forEach((dot) => {
      dot.classList.remove('is-active');
      dot.setAttribute('aria-current', 'false');
    });

    const activeDot = root.querySelector(`[data-featured-dot="${index}"]`);
    if (activeDot) {
      void activeDot.offsetWidth;
      activeDot.classList.add('is-active');
      activeDot.setAttribute('aria-current', 'true');
    }
  }

  function resetCarouselCursor(cursor) {
    if (!(cursor instanceof HTMLElement)) {
      return;
    }

    cursor.classList.remove('is-active');
    void cursor.offsetWidth;
    cursor.classList.add('is-active');
  }

  function isInteractiveTarget(target) {
    return target instanceof Element && Boolean(target.closest('a[href], button, input, select, textarea, summary'));
  }

  function syncSlides({ mediaSlides, copySlides }, previous, current) {
    mediaSlides.forEach((slide, index) => {
      slide.classList.toggle('is--current', index === current);
      slide.setAttribute('aria-hidden', String(index !== current));
    });

    copySlides[previous]?.classList.remove('is-active');
    copySlides[current]?.classList.add('is-active');
  }

  function instantShow(root, state, index) {
    state.current = index;
    state.mediaSlides.forEach((slide, slideIndex) => {
      slide.classList.toggle('is--current', slideIndex === index);
      slide.style.opacity = '';
      slide.style.transform = '';
      slide.setAttribute('aria-hidden', String(slideIndex !== index));
    });
    state.mediaInners.forEach((inner) => {
      inner.style.transform = '';
    });
    state.copySlides.forEach((slide, slideIndex) => {
      slide.classList.toggle('is-active', slideIndex === index);
      if (slideIndex === index) {
        clearTargets(slide);
      } else {
        setTargetsHidden(slide);
      }
    });
    resetTimerDots(root, index);
  }

  function directionFor(current, next, length) {
    if (current === length - 1 && next === 0) {
      return 1;
    }

    if (current === 0 && next === length - 1) {
      return -1;
    }

    return next > current ? 1 : -1;
  }

  function initFeaturedProduct(root) {
    if (root.dataset.featuredProductReady === 'true') {
      return;
    }

    root.dataset.featuredProductReady = 'true';

    const section = root.querySelector('.arb-featured-product');
    const mediaWrap = root.querySelector('[data-slideshow="wrap"]');
    const state = {
      mediaSlides: Array.from(root.querySelectorAll('[data-slideshow="slide"]')),
      mediaInners: Array.from(root.querySelectorAll('[data-slideshow="parallax"]')),
      copySlides: Array.from(root.querySelectorAll('.arb-featured-product__slide')),
      dots: Array.from(root.querySelectorAll('[data-featured-dot]')),
      current: 0,
      animating: false,
      timer: null,
      hoverPaused: false,
      focusPaused: false,
      userPaused: false,
    };

    if (!(section instanceof HTMLElement) || !(mediaWrap instanceof HTMLElement) || state.mediaSlides.length === 0 || state.copySlides.length === 0) {
      return;
    }

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const autoplayMs = Number(root.getAttribute('data-featured-autoplay')) || 0;
    const cursor = section.querySelector('[data-carousel-cursor]');
    const duration = 900;
    const wipeEase = 'cubic-bezier(0.6, 0.08, 0.02, 0.99)';

    const clearAutoplay = () => {
      if (state.timer) {
        window.clearTimeout(state.timer);
        state.timer = null;
      }
    };

    const scheduleAutoplay = () => {
      clearAutoplay();

      if (
        reducedMotion
        || document.hidden
        || state.hoverPaused
        || state.focusPaused
        || state.userPaused
        || autoplayMs <= 0
        || state.mediaSlides.length < 2
      ) {
        return;
      }

      state.timer = window.setTimeout(() => {
        if (!state.animating) {
          navigate(1);
        }
      }, autoplayMs);
    };

    const finishSlide = (index) => {
      state.animating = false;
      state.current = index;
      resetTimerDots(root, index);
      resetCarouselCursor(cursor);
      scheduleAutoplay();
    };

    const animateInitial = () => {
      state.copySlides.forEach((slide, index) => {
        slide.classList.toggle('is-active', index === state.current);
        clearTargets(slide);
      });

      resetTimerDots(root, state.current);
      resetCarouselCursor(cursor);
      scheduleAutoplay();
    };

    function navigate(direction, targetIndex = null) {
      if (state.animating || state.mediaSlides.length < 2) {
        return;
      }

      const previous = state.current;
      const next = targetIndex !== null && targetIndex !== undefined
        ? Number(targetIndex)
        : direction === 1
          ? previous < state.mediaSlides.length - 1
            ? previous + 1
            : 0
          : previous > 0
            ? previous - 1
            : state.mediaSlides.length - 1;

      if (previous === next || Number.isNaN(next)) {
        return;
      }

      clearAutoplay();

      if (reducedMotion) {
        instantShow(root, state, next);
        scheduleAutoplay();
        return;
      }

      state.animating = true;
      direction = directionFor(previous, next, state.mediaSlides.length);

      const currentSlide = state.mediaSlides[previous];
      const currentInner = state.mediaInners[previous];
      const upcomingSlide = state.mediaSlides[next];
      const upcomingInner = state.mediaInners[next];
      const previousCopy = state.copySlides[previous];
      const upcomingCopy = state.copySlides[next];
      const previousTargets = getCopyTargets(previousCopy);
      const upcomingTargets = getCopyTargets(upcomingCopy);

      upcomingSlide.classList.add('is--current');
      upcomingSlide.setAttribute('aria-hidden', 'false');
      setTargetsHidden(upcomingCopy);

      animateElement(
        currentSlide,
        [
          { opacity: 1, transform: 'translateX(0)' },
          { opacity: 0, transform: `translateX(${-direction * 100}%)` },
        ],
        { duration, easing: wipeEase },
      );

      if (currentInner) {
        animateElement(
          currentInner,
          [
            { transform: 'translateX(0)' },
            { transform: `translateX(${direction * 50}%)` },
          ],
          { duration, easing: wipeEase },
        );
      }

      animateElement(
        upcomingSlide,
        [
          { opacity: 0, transform: `translateX(${direction * 100}%)` },
          { opacity: 1, transform: 'translateX(0)' },
        ],
        { duration, easing: wipeEase },
      );

      if (upcomingInner) {
        animateElement(
          upcomingInner,
          [
            { transform: `translateX(${-direction * 50}%)` },
            { transform: 'translateX(0)' },
          ],
          { duration, easing: wipeEase },
        );
      }

      previousTargets.forEach((target, index) => {
        animateElement(
          target,
          [
            { opacity: 1, transform: 'translateY(0)' },
            { opacity: 0, transform: 'translateY(-1.25em)' },
          ],
          {
            duration: 320,
            delay: index * 60,
            easing: 'cubic-bezier(0.4, 0, 1, 1)',
          },
        );
      });

      window.setTimeout(() => {
        syncSlides(state, previous, next);
        previousTargets.forEach((target) => {
          target.style.opacity = '';
          target.style.transform = '';
          target.style.visibility = '';
        });
        upcomingTargets.forEach((target) => {
          target.style.visibility = 'visible';
        });

        upcomingTargets.forEach((target, index) => {
          animateElement(
            target,
            [
              { opacity: 0, transform: 'translateY(1.5em)' },
              { opacity: 1, transform: 'translateY(0)' },
            ],
            {
              duration: 650,
              delay: index * 100,
              easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
            },
          ).finished.then(() => {
            target.style.opacity = '';
            target.style.transform = '';
            target.style.visibility = '';
          }).catch(() => {});
        });
      }, 280);

      window.setTimeout(() => {
        currentSlide.classList.remove('is--current');
        currentSlide.style.opacity = '';
        currentSlide.style.transform = '';
        currentSlide.setAttribute('aria-hidden', 'true');
        upcomingSlide.style.opacity = '';
        upcomingSlide.style.transform = '';
        if (currentInner) {
          currentInner.style.transform = '';
        }
        if (upcomingInner) {
          upcomingInner.style.transform = '';
        }
        finishSlide(next);
      }, duration);
    }

    state.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const targetIndex = Number(dot.getAttribute('data-featured-dot'));
        if (targetIndex === state.current || state.animating) {
          return;
        }

        navigate(targetIndex > state.current ? 1 : -1, targetIndex);
      });
    });

    section.addEventListener('pointermove', (event) => {
      if (!(cursor instanceof HTMLElement)) {
        return;
      }

      const rect = section.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      const isHalfStart = x < rect.width / 2;

      if (isInteractiveTarget(event.target)) {
        cursor.classList.remove('is-visible', 'is-half-start', 'is-half-end');
        section.classList.remove('is-hover-prev', 'is-hover-next');
        return;
      }

      cursor.classList.toggle('is-half-start', isHalfStart);
      cursor.classList.toggle('is-half-end', !isHalfStart);
      cursor.classList.add('is-visible');
      cursor.style.translate = `${(x - cursor.clientWidth / 2).toFixed(3)}px ${(y - cursor.clientHeight / 2).toFixed(3)}px`;

      section.classList.toggle('is-hover-prev', isHalfStart);
      section.classList.toggle('is-hover-next', !isHalfStart);
    });

    section.addEventListener('pointerleave', () => {
      cursor?.classList.remove('is-visible', 'is-half-start', 'is-half-end');
      section.classList.remove('is-hover-prev', 'is-hover-next');
    });

    section.addEventListener('click', (event) => {
      if (isInteractiveTarget(event.target) || state.animating) {
        return;
      }

      const rect = section.getBoundingClientRect();
      const direction = event.clientX - rect.left < rect.width / 2 ? -1 : 1;
      navigate(direction);
    });

    root.addEventListener('mouseenter', () => {
      state.hoverPaused = true;
      clearAutoplay();
    });

    root.addEventListener('mouseleave', () => {
      state.hoverPaused = false;
      cursor?.classList.remove('is-visible', 'is-half-start', 'is-half-end');
      section.classList.remove('is-hover-prev', 'is-hover-next');
      scheduleAutoplay();
    });

    // WCAG 2.2.2: pause auto-rotation while keyboard focus is anywhere inside
    // the carousel, so focus never gets pulled out from under the user.
    root.addEventListener('focusin', () => {
      state.focusPaused = true;
      clearAutoplay();
    });

    root.addEventListener('focusout', (event) => {
      if (event.relatedTarget instanceof Node && root.contains(event.relatedTarget)) {
        return;
      }

      state.focusPaused = false;
      scheduleAutoplay();
    });

    // WCAG 2.2.2: visible, keyboard-operable pause/play toggle rendered next
    // to the numbered dots (see the section Liquid). Labels come from the
    // theme locale via data attributes so the JS ships no hardcoded English.
    const pauseToggle = root.querySelector('[data-carousel-pause]');
    if (pauseToggle) {
      pauseToggle.addEventListener('click', () => {
        state.userPaused = !state.userPaused;
        pauseToggle.classList.toggle('is-paused', state.userPaused);
        root.classList.toggle('is-autoplay-paused', state.userPaused);
        // 4.1.2: the class swaps the glyph, aria-pressed carries the state to
        // anyone who cannot see it. Both are needed — the glyph alone announces
        // nothing, and aria-pressed alone leaves the icon lying.
        pauseToggle.setAttribute('aria-pressed', state.userPaused ? 'true' : 'false');

        const label = state.userPaused
          ? pauseToggle.getAttribute('data-label-play')
          : pauseToggle.getAttribute('data-label-pause');
        if (label) {
          pauseToggle.setAttribute('aria-label', label);
        }

        if (state.userPaused) {
          clearAutoplay();
        } else {
          // An explicit "play" wins over the implicit focus pause (the toggle
          // itself is focused right now); tabbing further inside re-pauses.
          state.focusPaused = false;
          scheduleAutoplay();
        }
      });
    }

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        clearAutoplay();
        return;
      }

      scheduleAutoplay();
    });

    animateInitial();
  }

  ready(() => {
    document.querySelectorAll('.arb-featured-product-wrap[data-featured-product]').forEach(initFeaturedProduct);
  });

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll?.('.arb-featured-product-wrap[data-featured-product]').forEach(initFeaturedProduct);
  });
})();
